import graphql__f43e0de168cbaea83af9 from "./__generated__/CommentForm_comment.graphql.js";import { useCallback, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import { useAlert } from "../Alerts/withAlert";
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from "../Alerts/Alert";
import Modal from "../Modals/Modal";
import WysiwygField from "../Form/Field/WysiwygField";
import Button from "../Buttons/Button";
import CommentFormSubmit from "./CommentFormSubmit";
import AnonymousContributionFormBanner from "../Contributions/AnonymousContribution/AnonymousContributionFormBanner";
import withFormStore from "../Form/withFormStore";
import { useFormatMessage } from "../Intl/Context";

import { submitComment } from "../../mutations/SubmitCommentMutation";
import { finalizeCommentable } from "../Utils/finalizeCommentable";

const CommentForm = props => {
  const comment = useFragment(CommentForm_comment, props.comment);
  const { commentable, contentAttachmentToken } = comment;

  const content = comment.content || "";

  const { validateForm, formState, onClose } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);

  const t = useFormatMessage();
  const title = t(intlMessages.title);
  const submit = t(intlMessages.submit);
  const cancel = t(intlMessages.cancel);
  const signInMessage = t(intlMessages.signInMessage);
  const successMessage = t(intlMessages.successMessage);

  const { showAlert } = useAlert();

  const breadcrumbs = [{ title }];

  const formClassName = classNames(
    "comment-form",
    "box",
    "padding-x-lg",
    "padding-y-md",
    {
      loading: isSubmitting
    }
  );

  const handleCommentResponse = useCallback(
    response => {
      setIsSubmitting(false);

      if (response.submitComment.success) {
        showAlert({
          message: successMessage,
          type: ALERT_TYPE_SUCCESS
        });

        onClose();

        if (response.submitComment.finalizeUrl) {
          finalizeCommentable(response.submitComment.finalizeUrl, (message, error) => {
            if (error) {
              showAlert({
                message: message,
                type: ALERT_TYPE_ERROR
              });
            }
          });
        }
      } else {
        showAlert({
          message: response.submitComment.notice,
          type: ALERT_TYPE_ERROR
        });
      }
    },
    [onClose, showAlert, successMessage]
  );

  const submitForm = useCallback(
    e => {
      // Avoid actual HTML form submission
      if (e && e.preventDefault) {
        e.preventDefault(e);
      }

      if (validateForm()) {
        submitComment(
          commentable,
          formState.fields.commentContent.value,
          handleCommentResponse,
          { anonymous: isAnonymous }
        );

        setIsSubmitting(true);
      }
    },
    [
      commentable,
      formState.fields.commentContent,
      handleCommentResponse,
      isAnonymous,
      validateForm
    ]
  );

  return (
    <Modal
      onRequestClose={onClose}
      breadcrumbs={breadcrumbs}
      title={title}
      className="modal-sm"
    >
      <AnonymousContributionFormBanner
        anonymousMode={comment}
        anonymous={isAnonymous}
        onChange={setIsAnonymous}
      />

      <form className={formClassName} onSubmit={submitForm}>
        <WysiwygField
          fieldName="commentContent"
          label={false}
          required
          defaultValue={content}
          attachmentToken={contentAttachmentToken}
        />

        <CommentFormSubmit
          authorization={commentable.authorizations.submitComment}
          signInMessage={signInMessage}
          label={submit}
          validateForm={validateForm}
          onSignIn={submitForm}
          className="btn-block-md box-shadow"
          isSubmitting={isSubmitting}
        />

        <Button
          className="btn btn-primary-inverse btn-block-md box-shadow"
          type="button"
          onClick={onClose}
          label={cancel}
          iconClassName="icon-arrow-right"
          iconPosition="after"
          data-loading={isSubmitting}
          disabled={isSubmitting}
          data-comment-close-button
        />
      </form>
    </Modal>
  );
};

const CommentForm_comment = graphql__f43e0de168cbaea83af9;

export default withFormStore(CommentForm);

const intlMessages = defineMessages({
  title: {
    id: "comments.form.title",
    defaultMessage: "Nouveau commentaire"
  },
  submit: {
    id: "comments.form.submit",
    defaultMessage: "Soumettre mon commentaire"
  },
  cancel: {
    id: "comments.form.cancel",
    defaultMessage: "Annuler"
  },
  signInMessage: {
    id: "comments.form.signInMessage",
    defaultMessage: "Merci de vous identifier pour pouvoir créer votre commentaire"
  },
  successMessage: {
    id: "comments.form.successMessage",
    defaultMessage: "Votre commentaire a bien été ajouté"
  }
});
